/**
 * Cross-tab + same-tab plan-change signal for instant dashboard freshness after an upgrade
 * (the unarchive runs on the Stripe webhook, 1-2s behind the user). No permanent background
 * polling — this only nudges an existing fetch.
 *
 *  - broadcastPlanChanged(): call right after a successful upgrade. Sets a durable localStorage
 *    flag (same-tab handoff across a navigation) AND posts to a BroadcastChannel (live cross-tab),
 *    with the storage event as the cross-tab fallback when BroadcastChannel is unavailable.
 *  - subscribePlanChanged(cb): live cross-tab listener (BroadcastChannel + storage). Returns an
 *    unsubscribe fn.
 *  - consumeRecentPlanChange(): one-shot check on mount for a recent same-tab flag (the tab that
 *    upgraded navigated away to /upgrade and back, so it can't receive the live signal).
 */
const CHANNEL = 'sb_plan_sync';
const FLAG_KEY = 'sb_plan_changed_at';

export function broadcastPlanChanged() {
  const ts = String(Date.now());
  try { localStorage.setItem(FLAG_KEY, ts); } catch { /* private mode */ }
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage({ type: 'plan-changed', ts });
    bc.close();
  } catch { /* no BroadcastChannel — storage event covers cross-tab */ }
}

export function subscribePlanChanged(cb) {
  let bc = null;
  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => { if (e?.data?.type === 'plan-changed') cb(); };
  } catch { /* fall back to storage only */ }
  const onStorage = (e) => { if (e.key === FLAG_KEY && e.newValue) cb(); };
  window.addEventListener('storage', onStorage);
  return () => {
    try { if (bc) bc.close(); } catch { /* ignore */ }
    window.removeEventListener('storage', onStorage);
  };
}

export function consumeRecentPlanChange(maxAgeMs = 30000) {
  try {
    const v = localStorage.getItem(FLAG_KEY);
    if (!v) return false;
    localStorage.removeItem(FLAG_KEY);
    const age = Date.now() - Number(v);
    return age >= 0 && age <= maxAgeMs;
  } catch {
    return false;
  }
}
